import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { candidatesAPI, jobsAPI } from '../api/client';
import { toast } from 'react-toastify';
import '../pages/AnimationsAndStyles.css';
import Breadcrumb from "../components/Breadcrumb";

const inrFormat = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

const PIPELINE_STAGES = [
  { key: 'screening', label: 'Screening', statuses: ['applied', 'screen_pending', 'screen_selected'], color: '#6366F1' },
  { key: 'l1', label: 'L1 Interview', statuses: ['l1_scheduled', 'l1_select'], color: '#0EA5E9' },
  { key: 'l2', label: 'L2 Interview', statuses: ['l2_scheduled', 'l2_select', 'shared_to_client', 'hr_round'], color: '#F59E0B' },
  { key: 'offer', label: 'Offer', statuses: ['col_issued', 'col_accepted', 'fol_issued', 'fol_accepted'], color: '#8B5CF6' },
  { key: 'joined', label: 'Joined', statuses: ['joined'], color: '#10B981' },
  { key: 'rejected', label: 'Rejected', statuses: ['screen_rejected', 'l1_reject', 'l2_reject', 'col_rejected', 'fol_rejected', 'candidate_backed', 'drop', 'cancelled'], color: '#EF4444' },
];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  const [recruiterFilter, setRecruiterFilter] = useState('');
  const [showRecruiterDropdown, setShowRecruiterDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recruiterDropdownRef = useRef(null);

  // Clicking anywhere outside the recruiter filter closes its dropdown
  // (native <select> filters already close on outside click).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recruiterDropdownRef.current && !recruiterDropdownRef.current.contains(event.target)) {
        setShowRecruiterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [jobsRes, candRes] = await Promise.all([
          jobsAPI.getAll(),
          candidatesAPI.getAll()
        ]);
        const found = jobsRes.data.find(j => j.id === parseInt(id));
        if (!found) {
          toast.error('Job not found');
          navigate('/jobs', { state: { activeView: 'list' } });
          return;
        }
        setJob(found);
        const filtered = candRes.data.filter(c => c.job_id === parseInt(id));
        setCandidates(filtered);
      } catch (err) {
        toast.error('Failed to load job details');
        navigate('/jobs', { state: { activeView: 'list' } });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const stageCounts = useMemo(() => {
    const counts = {};
    PIPELINE_STAGES.forEach(s => counts[s.key] = { key: s.key, label: s.label, color: s.color, statuses: s.statuses, count: 0 });
    candidates.forEach(c => {
      const stage = PIPELINE_STAGES.find(s => s.statuses.includes(c.status));
      if (stage) counts[stage.key].count++;
    });
    return Object.values(counts);
  }, [candidates]);

  const availableRecruiters = [...new Set(candidates.map(c => c.owner_name || c.assigned_to_name).filter(Boolean))];

  const filteredCandidates = useMemo(() => {
    let result = candidates;
    if (selectedStage) {
      result = result.filter(c => selectedStage.statuses.includes(c.status));
    }
    if (recruiterFilter) {
      result = result.filter(c => (c.owner_name || c.assigned_to_name) === recruiterFilter);
    }
    return result;
  }, [candidates, selectedStage, recruiterFilter]);

  const CANDIDATES_PER_PAGE = 15;
  const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / CANDIDATES_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedCandidates = filteredCandidates.slice(
    (safeCurrentPage - 1) * CANDIDATES_PER_PAGE,
    safeCurrentPage * CANDIDATES_PER_PAGE
  );
  const visiblePageNumbers = Array.from(
    { length: Math.min(4, totalPages) },
    (_, index) => {
      const startPage = Math.min(
        Math.max(1, safeCurrentPage - 1),
        Math.max(1, totalPages - 3)
      );
      return startPage + index;
    }
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStage, recruiterFilter]);

  if (loading) {
    return (
      <div style={page}>
        <div className="card"><p style={{ padding: 40, textAlign: 'center' }}>Loading job details...</p></div>
      </div>
    );
  }

  if (!job) return null;

  const remaining = (job.positions ?? 1) - (job.filled_positions ?? 0);
  const reqApproxRaw = job.requirement_approx_value;
  const reqApproxNum = reqApproxRaw == null || reqApproxRaw === '' ? NaN : Number(reqApproxRaw);
  const reqApproxText = Number.isFinite(reqApproxNum) ? inrFormat.format(reqApproxNum) : '-';

  return (
    <div style={page}>
      <style>{css}</style>

      <Breadcrumb items={[{ label: "Jobs", to: "/jobs" }, { label: job?.title || "Job Detail" }]} />
      {/* HEADER */}
      <div className="jd-header">
        <div>
          <button className="jd-back-btn" onClick={() => navigate('/jobs', { state: { activeView: location.state?.activeView || 'list' } })}>← Back to Openings</button>
          <h1 className="jd-title">{job.client_name} — {job.title}</h1>
        </div>
      </div>

      {/* KPI METRICS */}
          <div className="jd-metrics">
            <div className="jd-metric-card"><span className="jd-metric-value">{job.positions ?? 1}</span><span className="jd-metric-label">Total Positions</span></div>
            <div className="jd-metric-card"><span className="jd-metric-value">{job.total_candidates ?? 0}</span><span className="jd-metric-label">Submitted</span></div>
            <div className="jd-metric-card"><span className="jd-metric-value">{job.joined_count ?? 0}</span><span className="jd-metric-label">Joined</span></div>
            <div className="jd-metric-card"><span className="jd-metric-value">{job.rejected_count ?? 0}</span><span className="jd-metric-label">Rejected</span></div>
            <div className="jd-metric-card"><span className="jd-metric-value">{Math.max(0, remaining)}</span><span className="jd-metric-label">Remaining</span></div>
          </div>

          {/* JOB DETAILS */}
          <div className="jd-table-section">
            <h2 className="jd-section-title">Job Details</h2>
            <div className="jd-details-grid">
              <div className="jd-detail-item"><span className="jd-detail-label">Request By</span><span className="jd-detail-value">{job.request_by || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Created By</span><span className="jd-detail-value">{job.created_by || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Job Date</span><span className="jd-detail-value">{job.job_date ? new Date(job.job_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Created On</span><span className="jd-detail-value">{job.created_at ? new Date(job.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Status</span><span className={`jd-status-badge status-${job.status || 'open'}`}>{job.status || 'open'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Priority</span><span className="jd-detail-value">{job.priority || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Department</span><span className="jd-detail-value">{job.department || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Location</span><span className="jd-detail-value">{job.location || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">CTC Range</span><span className="jd-detail-value">{job.ctc_range || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Requirement Approx Value</span><span className="jd-detail-value">{reqApproxText}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Experience</span><span className="jd-detail-value">{job.experience_required || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Recruiter</span><span className="jd-detail-value">{job.recruit_manager || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Hiring Manager</span><span className="jd-detail-value">{job.hiring_manager_name || '-'}</span></div>
              <div className="jd-detail-item"><span className="jd-detail-label">Hiring Manager Email</span><span className="jd-detail-value">{job.hiring_manager_email || '-'}</span></div>
            </div>
          </div>

          {/* PIPELINE STAGE FILTERS */}
          <div className="jd-pipeline">
            <div className="jd-pipeline-header">
              <h2 className="jd-section-title" style={{ margin: 0 }}>Pipeline by Stage</h2>
              <div className="filter-dropdown" ref={recruiterDropdownRef}>
                <button
                  type="button"
                  className={`filter-trigger ${recruiterFilter ? 'active' : ''}`}
                  onClick={() => setShowRecruiterDropdown(!showRecruiterDropdown)}
                >
                  <span>Recruiter</span>
                  <span className="filter-value">{recruiterFilter || 'All Recruiters'}</span>
                  <span className="filter-arrow">↓</span>
                </button>
                {showRecruiterDropdown && (
                  <div className="filter-menu">
                    <button
                      type="button"
                      className={`filter-option ${!recruiterFilter ? 'selected' : ''}`}
                      onClick={() => { setRecruiterFilter(''); setShowRecruiterDropdown(false); }}
                    >
                      All Recruiters
                    </button>
                    {availableRecruiters.map(name => (
                      <button
                        key={name}
                        type="button"
                        className={`filter-option ${recruiterFilter === name ? 'selected' : ''}`}
                        onClick={() => { setRecruiterFilter(name); setShowRecruiterDropdown(false); }}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="jd-stage-filters">
              <button
                className={`jd-stage-chip ${!selectedStage ? 'jd-stage-chip-active' : ''}`}
                onClick={() => setSelectedStage(null)}
              >
                All Candidates
                <span className="jd-stage-chip-count">{candidates.length}</span>
              </button>
              {stageCounts.map(stage => (
                <button
                  key={stage.key}
                  className={`jd-stage-chip ${selectedStage?.key === stage.key ? 'jd-stage-chip-active' : ''}`}
                  onClick={() => setSelectedStage(stage)}
                  style={{ '--chip-color': stage.color }}
                >
                  {stage.label}
                  <span className="jd-stage-chip-count">{stage.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* CANDIDATES TABLE */}
          <div className="jd-table-section">
            <div className="jd-table-header">
              <h2 className="jd-section-title" style={{ margin: 0 }}>
                {selectedStage ? `${selectedStage.label} Candidates` : 'All Candidates'}
              </h2>
              <span className="jd-table-count">{filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Candidate</th>
                    <th scope="col">Email</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Status</th>
                    <th scope="col">Recruiter</th>
                    <th scope="col" align="right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCandidates.map(c => (
                    <tr key={c.id}>
                      <td className="name">
                        <div className="avatar">{(c.first_name || 'C')[0]}</div>
                        <div>
                          <strong>{c.first_name} {c.last_name}</strong>
                          <div className="muted">ID #{c.id}</div>
                        </div>
                      </td>
                      <td>{c.email_id || '-'}</td>
                      <td>{c.phone_number || '-'}</td>
                      <td><span className={`status-badge ${c.status}`}>{c.status}</span></td>
                      <td>{c.owner_name || c.assigned_to_name || 'Unassigned'}</td>
                      <td align="right">
                        <button className="btn primary" onClick={() => navigate(`/candidates/${c.id}`)}>View</button>
                      </td>
                    </tr>
                  ))}
                  {filteredCandidates.length === 0 && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '32px 16px', color: '#94A3B8' }}>
                        No candidates in this stage
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination-wrap">
                <button
                  type="button"
                  className="pagination-btn pagination-arrow"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={safeCurrentPage === 1}
                  aria-label="Previous page"
                >
                  ←
                </button>
                {visiblePageNumbers.map(pageNumber => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`pagination-btn ${safeCurrentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  className="pagination-btn pagination-arrow"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={safeCurrentPage === totalPages}
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
            )}
          </div>

          {/* JOB DESCRIPTION */}
          {job.job_description && (
            <div className="jd-description-section">
              <h2 className="jd-section-title">Job Description</h2>
              <div className="jd-description-content">{job.job_description}</div>
            </div>
          )}
    </div>
  );
}

const page = {
  minHeight: 'auto',
  background: 'transparent',
  padding: '4px 0',
  fontFamily: 'Inter, sans-serif',
};

const css = `
.card { background: #fff; border-radius: 16px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); padding: 24px; }

/* Header */
.jd-header { margin-bottom: 16px; }
.jd-back-btn { background: none; border: 1px solid #E2E8F0; border-radius: 8px; padding: 5px 12px; cursor: pointer; font-size: 12px; color: #475569; margin-bottom: 10px; }
.jd-back-btn:hover { background: #F1F5F9; }
.jd-title { font-size: 20px; font-weight: 700; margin: 0 0 6px 0; color: #0F172A; }
.jd-meta { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; font-size: 12px; color: #64748B; }
.jd-status-badge { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 20px; text-transform: uppercase; }
.jd-status-badge.status-open { background: #DCFCE7; color: #16A34A; }
.jd-status-badge.status-closed { background: #FEE2E2; color: #DC2626; }
.jd-status-badge.status-hold { background: #FEF9C3; color: #CA8A04; }

/* KPI Metrics */
.jd-metrics { display: flex; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.jd-metric-card { flex: 1; min-width: 100px; background: #fff; border-radius: 10px; padding: 10px 10px; text-align: center; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.jd-metric-value { display: block; font-size: 20px; font-weight: 700; color: #0F172A; }
.jd-metric-label { display: block; font-size: 10px; color: #64748B; text-transform: uppercase; margin-top: 2px; letter-spacing: 0.3px; }

/* Job Details */
.jd-details-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.jd-detail-item { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px 12px; }
.jd-detail-label { display: block; font-size: 10px; color: #64748B; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px; }
.jd-detail-value { font-size: 13px; font-weight: 600; color: #0F172A; word-break: break-word; overflow-wrap: anywhere; }

/* Pipeline Filters */
.jd-pipeline { background: #fff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.jd-pipeline-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.jd-section-title { font-size: 14px; font-weight: 600; margin: 0 0 10px 0; color: #0F172A; }
.jd-pipeline-header .jd-section-title { margin: 0; }
.jd-stage-filters { display: flex; gap: 8px; flex-wrap: wrap; }
.jd-stage-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  color: #475569;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
}
.jd-stage-chip:hover { background: #F1F5F9; border-color: #CBD5E1; }
.jd-stage-chip-active {
  background: var(--chip-color, #1E40AF) !important;
  color: #fff !important;
  border-color: var(--chip-color, #1E40AF) !important;
  font-weight: 600;
}
.jd-stage-chip-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 600;
  padding: 0 5px;
}
.jd-stage-chip:not(.jd-stage-chip-active) .jd-stage-chip-count {
  background: #F1F5F9;
  color: #475569;
}
.jd-stage-chip-active .jd-stage-chip-count {
  background: rgba(255,255,255,0.2);
  color: #fff;
}

/* Table */
.jd-table-section { background: #fff; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.jd-table-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.jd-table-count { font-size: 12px; color: #64748B; font-weight: 500; }
.table-wrapper { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th { background: #F1F5F9; padding: 8px 8px; text-align: left; font-size: 12px; color: #475569; }
td { padding: 7px 8px; border-top: 1px solid #E5E7EB; font-size: 12px; }
.name { display: flex; gap: 8px; align-items: center; }
.avatar { width: 30px; height: 30px; background: #1E40AF; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; flex-shrink: 0; }
.muted { font-size: 11px; color: #64748B; }

.status-badge { padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
.status-badge.applied { background: #F1F5F9; color: #475569; }
.status-badge.screen_pending { background: #FEF9C3; color: #92400E; }
.status-badge.screen_selected { background: #DBEAFE; color: #1E40AF; }
.status-badge.screen_rejected { background: #FEE2E2; color: #991B1B; }
.status-badge.l1_scheduled,
.status-badge.l2_scheduled { background: #FEF3C7; color: #92400E; }
.status-badge.l1_select,
.status-badge.l2_select { background: #DCFCE7; color: #166534; }
.status-badge.l1_reject,
.status-badge.l2_reject { background: #FEE2E2; color: #991B1B; }
.status-badge.shared_to_client { background: #EDE9FE; color: #6D28D9; }
.status-badge.col_issued,
.status-badge.fol_issued { background: #DBEAFE; color: #1E40AF; }
.status-badge.col_accepted,
.status-badge.fol_accepted { background: #DCFCE7; color: #166534; }
.status-badge.col_rejected,
.status-badge.fol_rejected { background: #FEE2E2; color: #991B1B; }
.status-badge.joined { background: #DCFCE7; color: #166534; }
.status-badge.candidate_backed,
.status-badge.drop,
.status-badge.cancelled { background: #FEE2E2; color: #991B1B; }
.status-badge.hold { background: #FEF9C3; color: #92400E; }
.status-badge.hr_round { background: #EDE9FE; color: #6D28D9; }

.btn { padding: 6px 12px; border-radius: 8px; border: none; cursor: pointer; font-weight: 600; font-size: 13px; }

.filter-dropdown { position: relative; }
.filter-trigger {
  min-width: 170px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #CBD5E1;
  border-radius: 10px;
  background: #F8FAFC;
  color: #0F172A;
  cursor: pointer;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
}
.filter-trigger:hover { border-color: #94A3B8; background: #FFFFFF; }
.filter-trigger.active {
  border-color: #1D4ED8;
  background: #EFF6FF;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}
.filter-value { flex: 1; text-align: left; color: #475569; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.filter-arrow { color: #64748B; font-size: 12px; }
.filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  max-height: 240px;
  overflow-y: auto;
  background: #FFFFFF;
  border: 1px solid #CBD5E1;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  padding: 6px 0;
}
.filter-option {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 8px 12px;
  font-size: 13px;
  font-family: 'Inter', sans-serif;
  color: #334155;
  cursor: pointer;
  transition: background 0.2s ease;
}
.filter-option:hover { background: #F1F5F9; }
.filter-option.selected { background: #EFF6FF; color: #1D4ED8; font-weight: 700; }

.pagination-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.pagination-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #E2E8F0;
  background: #FFFFFF;
  color: #1E293B;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
}
.pagination-btn:hover:not(:disabled) { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
.pagination-btn.active {
  background: #0F4C81 !important;
  color: #fff !important;
  border-color: #0F4C81 !important;
  font-weight: 800 !important;
  text-decoration: underline !important;
}
.pagination-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.pagination-arrow { font-size: 24px; line-height: 1; }
.btn.primary { background: #1E40AF; color: white; }
.btn.primary:hover { background: #1e3a8a; }

/* Job Description */
.jd-description-section { background: #fff; border-radius: 16px; padding: 20px 24px; margin-bottom: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.jd-description-content { font-size: 14px; color: #334155; line-height: 1.7; white-space: pre-wrap; }

@media (max-width: 768px) {
  .jd-details-grid { grid-template-columns: 1fr; }
  .jd-metrics { gap: 8px; }
  .jd-metric-card { min-width: calc(50% - 8px); padding: 12px 8px; }
  .jd-metric-value { font-size: 22px; }
  .jd-title { font-size: 20px; }
  .jd-table-section, .jd-pipeline, .jd-description-section { padding: 16px; }
  .pagination-btn { width: 36px; height: 36px; font-size: 13px; }
  .pagination-arrow { font-size: 20px; }
}

@media (max-width: 480px) {
  .jd-metric-card { min-width: 100%; }
  .jd-stage-chip { font-size: 12px; padding: 5px 12px; }
}
`;
